import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
} from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import validationRules from '../../utils/validations.util';
import SnackbarInfo from '../common/snackbar-info.component';
import { managePostersById } from '../../services/playlists.service';
import { CDN_BASE_URL } from '../../config/const.config';
import Upload from '../common/upload.component';

const PlaylistPosters = ({
  playlistDetail,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    setValue,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useForm({
    defaultValues: {
      channels:
        playlistDetail.channels?.map((channel) => ({
          data: {
            id: channel.id || null,
            title: channel.name || '',
            code: channel.code || '',
          },
          thumbnail: channel.thumbnail || null,
        })) || [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'channels',
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [uploadedImg, setUploadedImg] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      channels: data.channels.map((channel) => ({
        id: channel.data.id,
        title: channel.data.title,
        code: channel.data.code,
        thumbnail: channel.thumbnail,
      })),
    };
    managePostersById(playlistDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Playlist posters updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form
        id="playlist-add-posters-form"
        onSubmit={handleSubmit(onFormSubmit)}
      >
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              container
              justifyContent="center"
              alignItems="center"
              spacing={2}
              key={field.id}
            >
              <Grid item xs={4}>
                {field.data?.id && (
                  <Typography variant="body2">
                    {field.data.code} - {field.data.title}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name={`channels.${index}.thumbnail`}
                  control={control}
                  rules={validationRules.OPTIONAL_URL}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Poster URL"
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      fullWidth
                      variant="filled"
                      error={!!errors?.channels?.[index]?.thumbnail}
                      helperText={
                        errors?.channels?.[index]?.thumbnail?.message || ''
                      }
                    />
                  )}
                />
                <Typography variant="body2" color="secondary" mt={1}>
                  {CDN_BASE_URL}
                </Typography>
                <Box mt={1}>
                  <Upload
                    title="Upload Poster URL"
                    updateImage={(v) => {
                      setValue(`channels.${index}.thumbnail`, v);
                      setUploadedImg(v);
                    }}
                    s3Folder="posters"
                    sourceType="poster16x9FileName"
                    aspectRatio="16.9"
                    allowedSize={null}
                    setSnackbarInfo={setSnackbarInfo}
                    type="poster"
                    details={playlistDetail}
                    from="playlist"
                  />
                  {uploadedImg && (
                    <img
                      src={uploadedImg}
                      style={{
                        maxHeight: '150px',
                        maxWidth: '150px',
                        objectFit: 'contain',
                      }}
                      alt="Uploaded"
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </form>

      <Stack direction="row" sx={{ py: 2.5 }}>
        <Button
          type="submit"
          form="playlist-add-posters-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Posters
        </Button>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

PlaylistPosters.propTypes = {
  playlistDetail: PropTypes.object.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default PlaylistPosters;
